import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import moment from 'moment'

import api from '../services/api'
import { getCookie } from '../utils/manageCookie'
import useModal from '../hooks/useModal'
import Utils from '../utils'

import { CompleteBtn } from '../components/Buttons'
import { Card, Typography, Divider } from '@mui/material'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TextField } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'


function StudySetting() {
  const { nickname } = getCookie('akive_user')
  const { openModal } = useModal()
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      attendOn: null,
      meal_1timeSec: null,
      rest_10minSec: '',
      mealNum: '',
    }
  })

  useEffect(() => {
    api.get(`/users/${nickname}/goals`)
      .then(result => {
        const date = moment().format('YYYY-MM-DD')
        reset({
          attendOn: moment(`${date} ${result.data.attendOn}`),
          meal_1timeSec: moment(`${date} ${String(parseInt(result.data.meal_1timeSec / 3600)).padStart(2, '0')}:${String(parseInt(result.data.meal_1timeSec % 3600 / 60)).padStart(2, '0')}`),
          rest_10minSec: parseInt(result.data.rest_10minSec / 60),
          mealNum: result.data.mealNum,
        })
      })
  }, [])

  async function modifyStudySetting({ attendOn, rest_10minSec, mealNum, meal_1timeSec }) {
    meal_1timeSec = moment(meal_1timeSec).format('HH:mm').split(':')
    meal_1timeSec = (meal_1timeSec[0] * 3600) + (meal_1timeSec[1] * 60)

    api.put(`/users/${nickname}/goals`, {
      attendOn: moment(attendOn).format('HH:mm:ss'),
      rest_10minSec: rest_10minSec * 60,
      mealNum,
      meal_1timeSec,
    })
    .then(() => openModal({ type: 'alert', props: { isError: false, contents: '수정되었습니다.' } }))
    .catch((err) => openModal({ type: 'alert', props: { isError: true, contents: err.response?.data.message || err.message } }))
  }

  return (
    <>
      <Container>
        <Box width='40%' height='auto'>
          {/* 출석 & 휴식 설정 */}
          <Card variant='outlined' sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography
              variant='h6'
              gutterBottom
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                lineHeight: 1.5,
              }}
            >
              <AccessTimeIcon color='primary' sx={{ fontSize: '1.25rem' }} />
              출석 및 휴식 설정
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'nowrap' }}>
              {/* 출석 시간 */}
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Controller
                  control={control}
                  name='attendOn'
                  render={({ field }) => (
                    <TimePicker
                      label='출석 인증 시간'
                      format='HH:mm'
                      value={field.value}
                      inputRef={field.ref}
                      onChange={field.onChange}
                      sx={{ width: '100%' }}
                    />
                  )}
                />
              </LocalizationProvider>

              {/* 휴식 시간 */}
              <Controller
                control={control}
                name='rest_10minSec'
                rules={{
                  required: '휴식 시간(분)을 입력하세요',
                  max: { value: 10, message: '10분을 초과할 수 없습니다' },
                  validate: (v) => Utils.isZeroOrPosInt(v) || '0보다 크거나 같은 정수여야 합니다.',
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label='10분당 휴식 시간(분)'
                    type='number'
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    value={field.value}
                    inputRef={field.ref}
                    onChange={field.onChange}
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </div>
          </Card>

          {/* 식사 설정 */}
          <Card variant='outlined' sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography
              variant='h6'
              gutterBottom
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                lineHeight: 1.5,
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: '1.25rem' }} color='primary' />
              식사 설정
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'nowrap' }}>
                {/* 회당 식사 시간 */}
                <Controller
                  control={control}
                  name='meal_1timeSec'
                  render={({ field }) => (
                    <TimeField
                      label='회당 식사 시간'
                      format='HH:mm'
                      value={field.value}
                      inputRef={field.ref}
                      onChange={field.onChange}
                      sx={{ width: '50%' }}
                    />
                  )}
                />

                {/* 식사 횟수 */}
                <Controller
                  control={control}
                  name='mealNum'
                  rules={{
                    required: '식사 횟수를 입력하세요',
                    max: { value: 10, message: '10회를 초과할 수 없습니다' },
                    validate: (v) => Utils.isZeroOrPosInt(v) || '0보다 크거나 같은 정수여야 합니다.',
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      label='식사 횟수'
                      type='number'
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      value={field.value}
                      inputRef={field.ref}
                      onChange={field.onChange}
                      sx={{ width: '50%' }}
                    />
                  )}
                />
              </div>
            </LocalizationProvider>
          </Card>


          <div style={{ textAlign: 'center' }}>
            <CompleteBtn
              onClick={handleSubmit(modifyStudySetting)}
              height='45px'
              width='150px'
            >
              수정 하기
            </CompleteBtn>
          </div>
        </Box>
      </Container>
    </>
  )
}

export const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
`

export const Box = styled.div`
    position: relative;
    width: ${props => props.width};
    height: ${props => props.height || 'auto'};
    background-color: #FEFEFE;
    padding: 3rem;
    border-radius: 16px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    text-align: center;
`

export default StudySetting
